import { memo, useEffect, useState, type ChangeEvent } from 'react';
import Skeleton from '@mui/material/Skeleton';
import Card from "@mui/material/Card";
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { SimplePagination } from 'ui/molecules/Pagination';
import type { Merchant } from 'features/merchants/types';
import Resource from 'ui/layouts/Resource';
import { WebRoutes } from 'consts/enpoints/web';
import Box from '@mui/material/Box';
import { Link, Outlet, useParams } from 'infrastructure/router';
import { Filters } from 'consts/transactions';
import { compareIds } from 'util/support';
import useFetchMerchantList from './useFetchMerchantList';
import type { MerchantListProps } from './types';

type StatusFilter = 'all' | 'active' | 'inactive';

const MerchantList = ({
    title,
    heading,
    links,
    Route,
    action,
    disabled,
    onClick,
    secondaryAction: SecondaryAction,
    searchable,
    pageSize,
    showStatusFilter = false
}: MerchantListProps) => {
    const params = useParams();
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [page, setPage] = useState(1);

    const { getActiveMerchantsSlice, isLoading } = useFetchMerchantList();

    const onSearchChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
        setSearch(target.value);
    };

    const onStatusFilterChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
        setStatusFilter(target.value as StatusFilter);
    };

    const normalizedSearch = search.trim().toLowerCase();
    const filteredMerchants = getActiveMerchantsSlice()
        .filter(({ fullName }: Merchant) =>
            !searchable || !normalizedSearch || fullName?.toLowerCase().includes(normalizedSearch))
        .filter(({ isActive }: Merchant) => {
            if (statusFilter === 'all') return true;
            return Boolean(isActive) === (statusFilter === 'active');
        });

    const isPaginated = Boolean(pageSize && pageSize > 0);
    const pageCount = isPaginated
        ? Math.max(1, Math.ceil(filteredMerchants.length / (pageSize as number)))
        : 1;
    const visibleMerchants = isPaginated
        ? filteredMerchants.slice((page - 1) * (pageSize as number), page * (pageSize as number))
        : filteredMerchants;

    useEffect(() => {
        setPage(1);
    }, [normalizedSearch, statusFilter]);

    useEffect(() => {
        if (page > pageCount) {
            setPage(pageCount);
        }
    }, [page, pageCount]);

    return (
        <Resource
            title={`Merchants | ${title}`}
            breadcrumbsProps={{
                heading,
                links: links.length > 0
                    ? [
                        { name: 'Merchants', href: WebRoutes.Merchants.List },
                        ...links
                    ]
                    : [],
                action
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: `minmax(min-content, ${SecondaryAction ? 350 : 252}px) 1fr`,
                    columnGap: '24px',
                    alignItems: 'flex-start',
                    width: '16vw'
                }}
            >
                <Card
                    sx={{
                        boxShadow: theme => theme.shadows[5],
                        width: '16vw'
                    }}
                >
                    {searchable && (
                        <Box sx={{ p: 2, pb: 1 }}>
                            <TextField
                                fullWidth
                                type='search'
                                size='small'
                                placeholder='Search'
                                value={search}
                                onChange={onSearchChange}
                                disabled={isLoading}
                                inputProps={{ 'data-test-id': 'merchants.search' }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <SearchIcon fontSize='small' />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Box>
                    )}
                    {showStatusFilter && (
                        <Box sx={{ px: 2, pb: 1 }}>
                            <Accordion
                                disableGutters
                                elevation={0}
                                square
                                sx={{
                                    '&:before': { display: 'none' },
                                    bgcolor: 'transparent'
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon fontSize='small' />}
                                    sx={{
                                        minHeight: 0,
                                        px: 1,
                                        '& .MuiAccordionSummary-content': { my: 0.5 }
                                    }}
                                    data-test-id='merchants.statusFilter.toggle'
                                >
                                    <Typography variant='body2'
                                        sx={{
                                        ml: 'auto'
                                    }}>Show only</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
                                    <RadioGroup
                                        value={statusFilter}
                                        onChange={onStatusFilterChange}
                                        name='merchant-status-filter'
                                    >
                                        <FormControlLabel
                                            value='all'
                                            control={<Radio size='small' />}
                                            label={<Typography variant='body2'>All</Typography>}
                                            disabled={isLoading}
                                        />
                                        <FormControlLabel
                                            value='active'
                                            control={<Radio size='small' />}
                                            label={<Typography variant='body2'>Active</Typography>}
                                            disabled={isLoading}
                                        />
                                        <FormControlLabel
                                            value='inactive'
                                            control={<Radio size='small' />}
                                            label={<Typography variant='body2'>Inactive</Typography>}
                                            disabled={isLoading}
                                        />
                                    </RadioGroup>
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    )}
                    <List component="nav" aria-label="merchants">
                        {isLoading && (
                            Array.from({ length: 10 }).map((_, index) => (
                                <ListItem
                                    key={index}
                                >
                                    <Skeleton
                                        variant="rounded"
                                        width="100%"
                                        height={50}
                                    />
                                </ListItem>
                            ))
                        )}
                        {!isLoading && filteredMerchants.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary='No merchants found'
                                    primaryTypographyProps={{
                                        color: 'text.secondary',
                                        align: 'center'
                                    }}
                                />
                            </ListItem>
                        )}
                        {visibleMerchants
                            .map(({ fullName, coreId, ...merchant }: Merchant) => (
                                <ListItemButton
                                    disabled={disabled}
                                    key={coreId}
                                    component={Link}
                                    to={Route.Merchant(coreId)}
                                    selected={compareIds(
                                        String(params[Filters.merchantId]),
                                        coreId
                                    )}
                                    onClick={event => onClick?.(event, coreId)}
                                    data-test-id={`merchants.card.${coreId}`}
                                >
                                    <ListItemText
                                        primary={fullName}
                                        primaryTypographyProps={{
                                            noWrap: true,
                                            title: fullName
                                        }}
                                    />
                                    {SecondaryAction && (
                                        <SecondaryAction
                                            {...merchant}
                                            coreId={coreId}
                                            fullName={fullName}
                                        />
                                    )}
                                </ListItemButton>
                            ))}
                    </List>
                    {isPaginated && !isLoading && pageCount > 1 && (
                        <Box sx={{ px: 2, py: 1 }}>
                            <SimplePagination
                                page={page}
                                pageCount={pageCount}
                                onChange={setPage}
                                disabled={disabled}
                            />
                        </Box>
                    )}
                </Card>
                <Outlet />
            </Box>
        </Resource>
    );
};

export default memo(MerchantList);
